// Клиентская auth-обвязка оператора платформы.
// Токены храним в localStorage — так же, как admin-панель. Refresh автоматически
// прокидывается через api()-обёртку при 401 (см. api.ts).

const ACCESS_KEY = 'sadmin.access_token';
const REFRESH_KEY = 'sadmin.refresh_token';
const USER_KEY = 'sadmin.user';

const API_ORIGIN = (import.meta.env.VITE_API_URL?.trim() || '').replace(/\/+$/, '');

export interface PlatformAdmin {
  id: string;
  email: string;
  name: string;
  role: string;
  lastLoginAt?: string | null;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: 'Bearer';
  user: PlatformAdmin;
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function getUser(): PlatformAdmin | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PlatformAdmin;
  } catch {
    return null;
  }
}

export function isAuthenticated(): boolean {
  return Boolean(getAccessToken());
}

export function saveSession(res: LoginResponse) {
  localStorage.setItem(ACCESS_KEY, res.access_token);
  localStorage.setItem(REFRESH_KEY, res.refresh_token);
  localStorage.setItem(USER_KEY, JSON.stringify(res.user));
}

/** Обновляет только access-token (при успешном refresh). */
export function updateAccessToken(token: string) {
  localStorage.setItem(ACCESS_KEY, token);
}

export function logout() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
  // Используем replace, чтобы кнопка "назад" не возвращала на защищённую страницу.
  window.location.replace('/login');
}

/**
 * Низкоуровневый логин — прямой вызов бекенда (без api() обёртки,
 * чтобы не ловить redirect на logout при неправильных данных).
 */
export async function performLogin(email: string, password: string): Promise<LoginResponse> {
  const res = await fetch(`${API_ORIGIN}/api/operator/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let message = `Ошибка ${res.status}`;
    try {
      const json = JSON.parse(text) as { error?: string };
      if (json.error) message = json.error;
    } catch {
      if (text) message = text;
    }
    throw new Error(message);
  }

  return (await res.json()) as LoginResponse;
}

/**
 * Пытается обменять refresh на новый access. Возвращает access или null.
 * Если refresh истёк — вызывающий должен разлогинить пользователя.
 */
export async function performRefresh(): Promise<string | null> {
  const refresh = getRefreshToken();
  if (!refresh) return null;
  try {
    const res = await fetch(`${API_ORIGIN}/api/operator/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refresh }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { access_token?: string };
    if (!data.access_token) return null;
    updateAccessToken(data.access_token);
    return data.access_token;
  } catch {
    return null;
  }
}
